import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router'
import {
  useNotificaciones,
  useContadorNoLeidas,
  useMarcarLeida,
  useMarcarTodasLeidas,
  useEliminarNotificacion,
  useEliminarTodas,
  type Notificacion,
  type TipoNotificacion,
} from '../hooks/useNotificaciones'
import {
  IconoCampana,
  IconoReloj,
  IconoIncidencias,
  IconoAvisos,
  IconoCerrar,
  IconoQuitar,
  IconoVacio,
} from './iconos'

/** Color + icono por tipo de notificación (coincide con el mockup aprobado). */
const ESTILO: Record<
  TipoNotificacion,
  { etiqueta: string; Icono: typeof IconoReloj; clase: string }
> = {
  aplazamiento: { etiqueta: 'Aplazamiento', Icono: IconoReloj, clase: 'bg-aviso-fondo text-aviso-icono' },
  incidencia: { etiqueta: 'Incidencia', Icono: IconoIncidencias, clase: 'bg-negativo-fondo text-negativo-tinta' },
  aviso: { etiqueta: 'Aviso', Icono: IconoAvisos, clase: 'bg-positivo-fondo text-positivo-tinta' },
}

/** "hace 5 min" / "hace 2 h" / "hace 3 d" a partir de un ISO. */
function hace(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime()
  const min = Math.round(ms / 60000)
  if (min < 1) return 'ahora mismo'
  if (min < 60) return `hace ${min} min`
  const horas = Math.round(min / 60)
  if (horas < 24) return `hace ${horas} h`
  const dias = Math.round(horas / 24)
  return `hace ${dias} d`
}

export function Campana() {
  const [abierto, setAbierto] = useState(false)
  const contenedor = useRef<HTMLDivElement>(null)
  const navegar = useNavigate()

  const { data } = useNotificaciones()
  const { data: contador } = useContadorNoLeidas()
  const marcarLeida = useMarcarLeida()
  const marcarTodas = useMarcarTodasLeidas()
  const eliminar = useEliminarNotificacion()
  const eliminarTodas = useEliminarTodas()

  const items = data?.items ?? []
  const noLeidas = contador?.noLeidas ?? 0

  // Cerrar al hacer clic fuera o con Escape.
  useEffect(() => {
    if (!abierto) return
    const fuera = (e: MouseEvent) => {
      if (contenedor.current && !contenedor.current.contains(e.target as Node)) setAbierto(false)
    }
    const tecla = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setAbierto(false)
    }
    document.addEventListener('mousedown', fuera)
    document.addEventListener('keydown', tecla)
    return () => {
      document.removeEventListener('mousedown', fuera)
      document.removeEventListener('keydown', tecla)
    }
  }, [abierto])

  const alPulsar = (n: Notificacion) => {
    if (!n.leida) marcarLeida.mutate(n.id)
    if (n.enlace) {
      setAbierto(false)
      navegar(n.enlace)
    }
  }

  return (
    <div className="relative" ref={contenedor}>
      <button
        type="button"
        onClick={() => setAbierto((v) => !v)}
        aria-label={`Notificaciones${noLeidas > 0 ? ` (${noLeidas} sin leer)` : ''}`}
        className="relative flex size-10 items-center justify-center rounded-xl border border-borde bg-superficie text-tinta transition-colors hover:bg-elevado"
      >
        <IconoCampana className="size-5" />
        {noLeidas > 0 ? (
          <span className="absolute -right-1.5 -top-1.5 flex min-w-[18px] items-center justify-center rounded-full bg-negativo-punto px-1 text-[11px] font-bold leading-[18px] text-white">
            {noLeidas > 99 ? '99+' : noLeidas}
          </span>
        ) : null}
      </button>

      {abierto ? (
        <div className="absolute right-0 z-50 mt-2 w-[min(380px,calc(100vw-2rem))] overflow-hidden rounded-2xl border border-borde bg-superficie shadow-xl">
          <div className="flex items-center justify-between border-b border-borde px-4 py-3">
            <h3 className="text-sm font-semibold text-tinta">Notificaciones</h3>
            <div className="flex items-center gap-3">
              {items.length > 0 ? (
                <>
                  <button
                    type="button"
                    onClick={() => marcarTodas.mutate()}
                    disabled={noLeidas === 0}
                    className="text-xs font-semibold text-acento transition-colors hover:text-acento-tinta disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    Marcar todas
                  </button>
                  <button
                    type="button"
                    onClick={() => eliminarTodas.mutate()}
                    aria-label="Borrar todas"
                    title="Borrar todas"
                    className="text-tinta-apagada transition-colors hover:text-negativo-tinta"
                  >
                    <IconoQuitar className="size-4" />
                  </button>
                </>
              ) : null}
            </div>
          </div>

          <div className="max-h-[60vh] overflow-y-auto">
            {items.length === 0 ? (
              <div className="flex flex-col items-center gap-2 px-4 py-10 text-center">
                <IconoVacio className="size-8 text-tinta-apagada" />
                <p className="text-sm text-tinta-2">No hay notificaciones</p>
              </div>
            ) : (
              items.map((n) => {
                const { etiqueta, Icono, clase } = ESTILO[n.tipo]
                return (
                  <div
                    key={n.id}
                    className={[
                      'group relative flex gap-3 border-b border-borde px-4 py-3 transition-colors',
                      n.leida ? '' : 'bg-acento-suave',
                    ].join(' ')}
                  >
                    <span
                      className={[
                        'mt-1.5 size-2 shrink-0 rounded-full',
                        n.leida ? 'bg-transparent' : 'bg-acento',
                      ].join(' ')}
                      aria-hidden
                    />
                    <div className={['flex size-9 shrink-0 items-center justify-center rounded-lg', clase].join(' ')}>
                      <Icono className="size-5" />
                    </div>
                    <button
                      type="button"
                      onClick={() => alPulsar(n)}
                      className="min-w-0 flex-1 text-left"
                    >
                      <span
                        className={['mb-0.5 inline-block rounded px-1.5 py-px text-[10px] font-bold uppercase tracking-wide', clase].join(' ')}
                      >
                        {etiqueta}
                      </span>
                      <p className="text-sm font-semibold text-tinta">{n.titulo}</p>
                      {n.cuerpo ? <p className="mt-0.5 text-xs text-tinta-2">{n.cuerpo}</p> : null}
                      <p className="mt-1 text-[11px] text-tinta-apagada">{hace(n.creadaEn)}</p>
                    </button>
                    <button
                      type="button"
                      onClick={() => eliminar.mutate(n.id)}
                      aria-label="Descartar"
                      className="absolute right-2 top-2 rounded p-1 text-tinta-apagada opacity-0 transition-opacity hover:text-negativo-tinta group-hover:opacity-100"
                    >
                      <IconoCerrar className="size-4" />
                    </button>
                  </div>
                )
              })
            )}
          </div>

          <button
            type="button"
            onClick={() => {
              setAbierto(false)
              navegar('/alertas')
            }}
            className="flex w-full items-center justify-center gap-2 border-t border-borde px-4 py-3 text-sm font-semibold text-acento transition-colors hover:bg-elevado"
          >
            Ver todas las alertas
          </button>
        </div>
      ) : null}
    </div>
  )
}
